// Phase 34c.3 — proportional bar of pref / capital / catch-up / promote.
//
// Re-used by:
//   * /investor distribution detail slide-over
//   * Preview-modal "Compute distribution" in DistributionsSection
//   * Partnership Detail run-detail slide-over (future)
//
// Inputs:
//   - tiers: { pref, return_of_capital, catch_up, promote } numbers
//   - total: optional explicit total (else sum of the four).
//   - showLegend: bool
//   - testid: optional override (defaults to dist-preview-tier-segment-bar)

const SEGMENTS = [
  { key: "pref",              label: "Pref",                color: "bg-indigo-500",  text: "text-indigo-700",  tint: "bg-indigo-50" },
  { key: "return_of_capital", label: "Return of capital",   color: "bg-sky-500",     text: "text-sky-700",     tint: "bg-sky-50" },
  { key: "catch_up",          label: "GP catch-up",         color: "bg-emerald-500", text: "text-emerald-700", tint: "bg-emerald-50" },
  { key: "promote",           label: "Promote",             color: "bg-violet-500",  text: "text-violet-700",  tint: "bg-violet-50" },
];

function fmtMoney(n) {
  return (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export const TIER_TINT = SEGMENTS.reduce(
  (acc, s) => ({ ...acc, [s.key]: { tint: s.tint, text: s.text, color: s.color, label: s.label } }),
  {},
);

export default function TierSegmentBar({
  tiers,
  total,
  showLegend = true,
  testid = "dist-preview-tier-segment-bar",
  compact = false,
}) {
  const values = SEGMENTS.map((s) => ({ ...s, amount: Number(tiers?.[s.key] || 0) }));
  const explicitTotal = Number(total || 0);
  const sum = explicitTotal || values.reduce((acc, v) => acc + v.amount, 0);
  const safeTotal = sum > 0 ? sum : 1; // avoid div/0; empty state handled below

  if (sum <= 0) {
    return (
      <div
        data-testid={testid}
        className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500"
      >
        No distribution computed yet
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      {/* Bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        {values.map((s) => {
          const pct = (s.amount / safeTotal) * 100;
          if (s.amount <= 0) return null;
          return (
            <div
              key={s.key}
              data-testid={`${testid}-seg-${s.key}`}
              className={`${s.color} h-full transition-all duration-300`}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${fmtMoney(s.amount)} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      {showLegend ? (
        <div className={`mt-3 grid gap-x-4 gap-y-2 ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-4"}`}>
          {values.map((s) => {
            const pct = (s.amount / safeTotal) * 100;
            return (
              <div key={s.key} className="flex items-baseline gap-2 text-xs">
                <span className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${s.color}`} />
                <div className="min-w-0">
                  <div className="font-medium text-slate-700">{s.label}</div>
                  <div className="tabular-nums text-slate-500">
                    {fmtMoney(s.amount)} · {pct.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
